import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  GAME_KINDS,
  gameKindLabel,
  type FriendLoungeResponse,
  type GameKind,
  type GuestSeatResponse,
} from '@awwyeah/shared';
import { KindIcon } from './components/KindIcon';
import {
  canStartAnother,
  challengeQuip,
  loungeStatusLine,
  memberName,
  opponentIdOf,
  sortLoungeGames,
  startErrorCopy,
} from './friendLounge';
import { ClaimedGuestGame, GuestShell } from './GuestPlayPage';
import {
  apiReadFriendGame,
  apiReadLounge,
  apiStartFriendGame,
  GuestLinkApiError,
} from './guestApi';

/**
 * `/play/lounge` — friend of the family. Somebody who once played with the household via a
 * link keeps a friend pass (a long-lived guest session); this is their front porch: their
 * games with the family, and a way to start another one with whoever they've played with.
 *
 * Deliberately ambient (`memory/no-demand-mechanics.md`): no streaks, no "it's been N days",
 * no badges for coming back. "Your move" is a fact. The only push in the whole loop is the
 * ordinary one the *member* gets when a friend starts a game with them.
 */
export function FriendLoungePage(): JSX.Element {
  const [lounge, setLounge] = useState<FriendLoungeResponse | null | 'loading' | 'error'>(
    'loading',
  );

  useEffect(() => {
    let cancelled = false;
    void apiReadLounge()
      .then((result) => {
        if (!cancelled) setLounge(result);
      })
      .catch(() => {
        if (!cancelled) setLounge('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (lounge === 'loading') return <GuestShell>Fluffing the cushions…</GuestShell>;
  if (lounge === 'error') {
    return (
      <GuestShell testId="friend-lounge-error">
        <p className="text-base text-fg">The lounge is having a moment.</p>
        <p className="text-sm text-fg-3">Try again in a bit.</p>
      </GuestShell>
    );
  }
  if (lounge === null) {
    return (
      <GuestShell testId="friend-lounge-stranger">
        <p className="text-lg font-semibold text-fg">We haven’t met yet 👀</p>
        <p className="text-sm text-fg-2">
          This is where friends of the family hang out. Somebody in the family has to send you a
          play link first — after one game, you’re in.
        </p>
      </GuestShell>
    );
  }
  return <Lounge lounge={lounge} />;
}

function Lounge({ lounge }: { lounge: FriendLoungeResponse }): JSX.Element {
  const navigate = useNavigate();
  const [memberId, setMemberId] = useState(lounge.members[0]?.id ?? '');
  const [busy, setBusy] = useState<GameKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const games = sortLoungeGames(lounge.games);
  const canStart = canStartAnother(lounge);
  const challenged = lounge.members.find((m) => m.id === memberId) ?? lounge.members[0];

  const start = useCallback(
    async (kind: GameKind) => {
      if (!challenged) return;
      setBusy(kind);
      setError(null);
      try {
        const seat = await apiStartFriendGame(kind, challenged.id);
        const id = seat.game?.id;
        if (!id) throw new Error('no game');
        navigate(`/play/g/${encodeURIComponent(id)}`, {
          state: { quip: challengeQuip(challenged.name, id) },
        });
      } catch (caught) {
        const code = caught instanceof GuestLinkApiError ? caught.code : 'unknown';
        setError(startErrorCopy(code, lounge.maxActive));
      } finally {
        setBusy(null);
      }
    },
    [challenged, navigate, lounge.maxActive],
  );

  return (
    <main
      className="mx-auto flex min-h-screen w-full max-w-md flex-col gap-6 px-4 py-8"
      data-testid="friend-lounge"
    >
      <header className="flex flex-col gap-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-fg-3">
          🎟️ Friend of the family
        </p>
        <h1 className="font-display text-3xl tracking-tight text-fg">Hey, {lounge.guestName}.</h1>
        <p className="text-sm text-fg-2">
          You’re officially a friend of the family. Perks include rematches, trash talk, and
          absolutely no chores.
        </p>
      </header>

      <section className="flex flex-col gap-3" aria-labelledby="friend-start-heading">
        <h2 id="friend-start-heading" className="text-base font-bold text-fg">
          {challenged ? `Start another game with ${challenged.name}` : 'Start another game'}
        </h2>
        {lounge.members.length > 1 && (
          <div className="flex flex-wrap gap-2" data-testid="friend-member-picker">
            {lounge.members.map((member) => (
              <button
                key={member.id}
                type="button"
                onClick={() => setMemberId(member.id)}
                data-selected={member.id === challenged?.id}
                className={`flex h-10 items-center gap-2 rounded-full border px-3 text-sm font-semibold ${
                  member.id === challenged?.id
                    ? 'border-line-strong bg-inverse text-inverse-fg'
                    : 'border-line text-fg-2'
                }`}
              >
                <span
                  className="h-3 w-3 rounded-full"
                  style={{ backgroundColor: member.color }}
                  aria-hidden="true"
                />
                {member.name}
              </button>
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 gap-2" data-testid="friend-kind-picker">
          {GAME_KINDS.map((kind) => (
            <button
              key={kind}
              type="button"
              disabled={!canStart || busy !== null}
              onClick={() => void start(kind)}
              data-testid="friend-kind-option"
              data-kind={kind}
              className="flex h-12 items-center justify-center gap-2 rounded-md border border-line text-sm font-semibold text-fg disabled:opacity-50"
            >
              <KindIcon kind={kind} size={16} />
              {busy === kind ? 'Sending…' : gameKindLabel(kind)}
            </button>
          ))}
        </div>
        <p className="text-xs text-fg-3">
          {canStart
            ? 'You go first — you asked. They’ll see it next time they look.'
            : lounge.members.length === 0
              ? 'Nobody to challenge just yet — the family will turn up.'
              : startErrorCopy('too_many', lounge.maxActive)}
        </p>
        {error && (
          <p className="text-sm text-fg" role="status" data-testid="friend-start-error">
            {error}
          </p>
        )}
      </section>

      <section className="flex flex-col gap-2" aria-labelledby="friend-games-heading">
        <h2 id="friend-games-heading" className="text-base font-bold text-fg">
          Your games with the family
        </h2>
        {games.length === 0 ? (
          <p className="rounded-md border border-dashed border-line p-4 text-sm text-fg-3">
            No games yet. Suspiciously quiet in here. 🦗
          </p>
        ) : (
          <ul className="flex flex-col gap-2" data-testid="friend-games">
            {games.map((entry) => {
              const opponent = memberName(lounge.members, opponentIdOf(entry));
              return (
                <li key={entry.game.id}>
                  <Link
                    to={`/play/g/${encodeURIComponent(entry.game.id)}`}
                    className={`flex items-center gap-3 rounded-md border px-3 py-3 ${
                      entry.yourMove ? 'border-line-strong bg-raised' : 'border-line'
                    }`}
                    data-testid="friend-game-row"
                    data-your-move={entry.yourMove}
                    data-finished={entry.finished}
                  >
                    <KindIcon kind={entry.game.kind} size={18} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-fg">
                        {gameKindLabel(entry.game.kind)} with {opponent}
                      </span>
                      <span className="block text-xs text-fg-3">
                        {loungeStatusLine(entry, opponent)}
                      </span>
                    </span>
                    {entry.yourMove && (
                      <span className="h-2.5 w-2.5 rounded-full bg-[#e2574c]" aria-hidden="true" />
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/*
        LATER (Dan, not yet — "not until I get this all figured out"): this is where a
        "Make it official — get your own AwwYeah" sign-up link and a "Run HomeBox on your own
        server" download link will go. Keep them here, below the games, and keep them quiet:
        an invitation, never a gate or a nag.
      */}
    </main>
  );
}

/**
 * `/play/g/:gameId` — one of a friend's matches, reached from the lounge (or straight after
 * challenging somebody). Same board as a claimed play link; the server only answers for a
 * match this friend sits in.
 */
export function FriendGamePage(): JSX.Element {
  const { gameId } = useParams<{ gameId: string }>();
  const location = useLocation();
  const quip = (location.state as { quip?: string } | null)?.quip ?? null;
  const [seat, setSeat] = useState<GuestSeatResponse | null | 'loading'>('loading');
  const [showQuip, setShowQuip] = useState(Boolean(quip));

  useEffect(() => {
    let cancelled = false;
    setSeat('loading');
    if (!gameId) {
      setSeat(null);
      return;
    }
    void apiReadFriendGame(gameId)
      .then((result) => {
        if (!cancelled) setSeat(result);
      })
      .catch(() => {
        if (!cancelled) setSeat(null);
      });
    return () => {
      cancelled = true;
    };
  }, [gameId]);

  useEffect(() => {
    if (!showQuip) return undefined;
    const t = setTimeout(() => setShowQuip(false), 4500);
    return () => clearTimeout(t);
  }, [showQuip]);

  if (seat === 'loading') return <GuestShell>Opening the game…</GuestShell>;
  if (!seat) {
    return (
      <GuestShell testId="friend-game-missing">
        <p className="text-base text-fg">This game isn’t here.</p>
        <Link to="/play/lounge" className="text-sm font-semibold text-fg-2 underline">
          Back to the lounge
        </Link>
      </GuestShell>
    );
  }
  return (
    <>
      {showQuip && quip && (
        <div
          role="status"
          className="fixed left-1/2 top-4 z-30 -translate-x-1/2 rounded-full border border-line bg-surface px-4 py-2 text-sm font-semibold text-fg shadow-lg"
          data-testid="friend-challenge-quip"
        >
          {quip}
        </div>
      )}
      <ClaimedGuestGame key={seat.game?.id ?? 'none'} seat={seat} />
    </>
  );
}
